import type { Events, EventsPayloads } from "./fiinder-api";
import type { ConnectionStatus } from "./fiinder-api-types";

// Define constants for REST events
const RestEvents = {
  SEND_REST_REQUEST: "send_rest_request",
  RECEIVE_REST_RESPONSE: "receive_rest_response",
} as const;

// Define constants for generic JSON request/response events
const JsonEvents = {
  RECEIVE_JSON_RESPONSE: "receive_json_response",
} as const;

interface PendingRequest {
  resolve: (data: any) => void;
  reject: (error: Error) => void;
}

export interface RestRequest {
  requestId: string;
  path: string;
  method?: string;
  params?: object;
  timeout?: number;
}

/**
 * Text of a socket reply's `error`, which the api sends either as a plain
 * string or as an object `{code, message}`.
 */
function errorText(error: string | { message: string }): string {
  return typeof error === "string" ? error : error.message;
}

/** The part of a `receive_rest_response` payload an error reply is read from. */
interface RestBridgeReply {
  status_code: number;
  body?: { detail?: string | { msg: string }[] };
}

/**
 * Text of a REST bridge error reply, read from `body.detail`: the string
 * itself, or the `msg` of each item when it is a validation error list.
 */
function restReplyErrorText(payload: RestBridgeReply): string {
  const detail = payload.body?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join("; ");
  return `Request failed with status ${payload.status_code}`;
}

/** Logs a server error reply and rejects the pending request with its text. */
function rejectWithServerError(
  reject: PendingRequest["reject"],
  message: string
): void {
  console.error(`Error received from server: ${message}`);
  reject(new Error(message));
}

export class WebsocketConnection {
  private websocket: WebSocket | null = null;
  private pendingRequests: { [requestId: string]: PendingRequest } = {};
  private eventListeners: { [eventType: string]: ((data: any) => void)[] } = {};

  private readonly maxReconnectAttempts = 10;
  private reconnectAttempts = 0;
  private connectionStatus: ConnectionStatus = "disconnected";
  private connectionStatusListeners = new Set<
    (status: ConnectionStatus) => void
  >();

  reconnectionAllowed = true;

  /**
   * Establish the WebSocket connection.
   */
  async connect(uri: string, getNewUri: () => Promise<string>): Promise<void> {
    return new Promise((resolve, reject) => {
      this.emitConnectionStatus(
        this.reconnectAttempts > 0 ? "reconnecting" : "connecting"
      );

      this.websocket = new WebSocket(uri);

      this.websocket.onopen = () => {
        // Set up the listener for incoming messages.
        this.websocket!.onmessage = (event) => this._messageListener(event);
        this.reconnectAttempts = 0;
        this.emitConnectionStatus("connected");
        resolve();
      };

      this.websocket.onerror = () => {
        this.emitConnectionStatus("disconnected");
        reject(new Error("WebSocket connection error"));
      };

      this.websocket.onclose = async () => {
        console.log("closing ....");

        this.websocket = null;

        if (this.reconnectionAllowed) {
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.reconnectionAllowed = false;
            this.emitConnectionStatus("disconnected");
            console.log("reconnection not allowed. Closed permanently.");
            return;
          }

          this.reconnectAttempts += 1;
          console.log("reconnecting ....");
          try {
            await this.connect(await getNewUri(), getNewUri);
          } catch (error) {
            console.error("WebSocket reconnection error:", error);
            if (this.reconnectAttempts >= this.maxReconnectAttempts) {
              this.reconnectionAllowed = false;
              this.emitConnectionStatus("disconnected");
            }
          }
        } else {
          console.log("reconnection not allowed. Closed permanently.");
        }
      };
    });
  }

  /**
   * Close the WebSocket connection.
   */
  close(): void {
    if (this.websocket) {
      this.reconnectionAllowed = false;
      this.websocket.close();
      this.websocket = null;
    }
    this.emitConnectionStatus("disconnected");
  }

  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  onConnectionStatusChange(listener: (status: ConnectionStatus) => void) {
    this.connectionStatusListeners.add(listener);
  }

  offConnectionStatusChange(listener: (status: ConnectionStatus) => void) {
    this.connectionStatusListeners.delete(listener);
  }

  private emitConnectionStatus(status: ConnectionStatus) {
    if (this.connectionStatus === status) return;
    this.connectionStatus = status;
    for (const listener of this.connectionStatusListeners) {
      listener(status);
    }
  }

  /**
   * Internal method to handle incoming messages.
   */
  private async _messageListener(event: MessageEvent): Promise<void> {
    try {
      const messageData = event.data;
      await this._processMessage(messageData);
    } catch (e) {
      console.error("WebSocket listener error:", e);
    }
  }

  /**
   * Process a received message.
   */
  private async _processMessage(messageData: any): Promise<void> {
    const data = JSON.parse(messageData);

    // Handle ping messages.
    if (data.type === "ping") {
      await this.sendJson({ type: "pong" });
      return;
    }

    // Handle REST responses.
    if (
      data.type === RestEvents.RECEIVE_REST_RESPONSE &&
      data.payload &&
      data.payload.request_id
    ) {
      const requestId = data.payload.request_id;
      if (this.pendingRequests[requestId]) {
        const { resolve, reject } = this.pendingRequests[requestId];
        delete this.pendingRequests[requestId];
        if (data.payload.error) {
          rejectWithServerError(reject, errorText(data.payload.error));
        } else if (data.payload.status_code >= 400) {
          rejectWithServerError(reject, restReplyErrorText(data.payload));
        } else {
          // Resolve with just the response data instead of the entire message
          resolve(data?.payload?.body);
        }
      }
      return;
    }

    // Handle generic JSON responses (for sendJson with request_id).
    if (data.type === JsonEvents.RECEIVE_JSON_RESPONSE && data.request_id) {
      const requestId = data.request_id;
      if (this.pendingRequests[requestId]) {
        const { resolve, reject } = this.pendingRequests[requestId];
        delete this.pendingRequests[requestId];
        if (data.error) {
          rejectWithServerError(reject, errorText(data.error));
        } else {
          // Resolve with the entire response data
          resolve(data);
        }
      }
      return;
    }

    // Handle any message with request_id as a potential response (fallback).
    // debugger;
    if (data.request_id && this.pendingRequests[data.request_id]) {
      const requestId = data.request_id;
      const { resolve, reject } = this.pendingRequests[requestId];
      delete this.pendingRequests[requestId];
      if (data.error) {
        rejectWithServerError(reject, errorText(data.error));
      } else {
        // Resolve with the entire response data
        resolve(data);
      }
      return;
    }

    // Dispatch to event listeners.
    const messageType = data.type;
    if (messageType && this.eventListeners[messageType]) {
      for (const listener of this.eventListeners[messageType]) {
        listener(data);
      }
    }
  }

  /**
   * Abstract method to handle requests with timeout and response waiting.
   */
  private async sendRequestWithResponse<T = any>(
    message: any,
    requestId: string,
    timeout: number = 30000 * 5
  ): Promise<T> {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      throw new Error("WebSocket is not connected");
    }

    this.websocket.send(JSON.stringify(message));

    return new Promise((resolve, reject) => {
      this.pendingRequests[requestId] = { resolve, reject };

      // Set up a timeout to reject the promise if no response is received.
      setTimeout(() => {
        if (this.pendingRequests[requestId]) {
          delete this.pendingRequests[requestId];
          reject(
            new Error(`Request ${requestId} timed out after ${timeout}ms`)
          );
        }
      }, timeout);
    });
  }

  /**
   * Send a JSON message over the WebSocket.
   * If requestId is provided, waits for a response with timeout.
   */
  async sendJson<T = any>(
    message: any,
    requestId?: string,
    timeout: number = 30000 * 5
  ): Promise<T | void> {
    if (requestId) {
      // Add request_id to the message for response tracking
      const messageWithId = { ...message, request_id: requestId };
      return this.sendRequestWithResponse<T>(messageWithId, requestId, timeout);
    } else {
      // Original behavior: fire and forget
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        throw new Error("WebSocket is not connected");
      }
      this.websocket.send(JSON.stringify(message));
    }
  }

  /**
   * Send a REST request via the WebSocket and await the response.
   *
   * @param requestId Unique identifier for the request.
   * @param path API endpoint path.
   * @param method HTTP method (GET, POST, etc.).
   * @param params Request parameters or body.
   * @param timeout Request timeout in milliseconds.
   * @returns The response data directly, not the full message object
   */
  async sendRestRequest<T extends any = any>({
    requestId,
    path,
    method = "GET",
    params = {},
    timeout = 30000 *5,
  }: RestRequest): Promise<T> {
    const message = {
      type: RestEvents.SEND_REST_REQUEST,
      request_id: requestId,
      path,
      method,
      params,
      timeout,
    };

    return this.sendRequestWithResponse<T>(message, requestId, timeout);
  }

  /**
   * Deprecated method: use event listeners instead.
   */
  async receiveJson(): Promise<any> {
    console.warn("receiveJson is deprecated. Use event listeners instead.");
    return new Promise((resolve, reject) => {
      if (!this.websocket) {
        return reject(new Error("WebSocket is not connected"));
      }
      this.websocket.onmessage = (event) => {
        resolve(JSON.parse(event.data));
      };
    });
  }

  /**
   * Register an event listener for a specific event type.
   */
  on<T extends keyof EventsPayloads>(
    eventType: T,
    callback: (data: Events<T>) => void
  ): void {
    if (!this.eventListeners[eventType]) {
      this.eventListeners[eventType] = [];
    }

    this.eventListeners[eventType] = [callback]; // Replace existing listeners with new one
  }

  /**
   * Remove an event listener for a specific event type.
   */
  off<T extends keyof EventsPayloads>(
    eventType: T,
    callback: (data: Events<T>) => void
  ): void {
    if (this.eventListeners[eventType]) {
      this.eventListeners[eventType] = this.eventListeners[eventType].filter(
        (listener) => listener !== callback
      );
    }
  }
}
